// 🔄 Общие типы для синхронизации Frontend-Backend Event Master v1.5
// Соответствует: TYPES_STRUCTURE.md

import { BudgetStatus } from "./events-unified";

// ===== СИСТЕМА УВЕДОМЛЕНИЙ =====

export enum NotificationType {
  SYSTEM = "system",
  EVENT = "event",
  TASK = "task",
  REFERRAL = "referral",
  ACHIEVEMENT = "achievement",
  REMINDER = "reminder",
  SOCIAL = "social",
  SECURITY = "security",
  // Новые типы для аналитики и AI
  ANALYTICS_REPORT_READY = "analytics_report_ready",
  ML_MODEL_TRAINED = "ml_model_trained",
  PREDICTION_AVAILABLE = "prediction_available",
  ANOMALY_DETECTED = "anomaly_detected",
  MARKETPLACE_QUOTE = "marketplace_quote",
  TEAM_INVITATION = "team_invitation",
  PARTNER_REQUEST = "partner_request",
  AI_SUGGESTION = "ai_suggestion",
}

export enum NotificationPriority {
  LOW = "low",
  MEDIUM = "medium",
  HIGH = "high",
  URGENT = "urgent",
  CRITICAL = "critical",
}

export enum NotificationChannel {
  IN_APP = "in_app",
  EMAIL = "email",
  SMS = "sms",
  PUSH = "push",
  TELEGRAM = "telegram",
  WHATSAPP = "whatsapp",
  WEBHOOK = "webhook",
  SLACK = "slack",
}

export enum NotificationStatus {
  UNREAD = "unread",
  READ = "read",
  DISMISSED = "dismissed",
}

export interface Notification {
  id: string;
  userId: string;
  type: NotificationType;
  priority: NotificationPriority;
  title: string;
  content: string;
  channels: NotificationChannel[];
  status: NotificationStatus;
  createdAt: string;
  readAt?: string;
  data?: Record<string, unknown>;
}

// ===== WEBSOCKET СОБЫТИЯ =====

export interface WebSocketEvents {
  // Client to Server
  "join-room": { room: string };
  "mark-notification-read": { notificationId: string };
  "subscribe-analytics": { dashboardId: string; widgetIds: string[] };
  "unsubscribe-analytics": { dashboardId: string };
  "join-team": { teamId: string };
  "leave-team": { teamId: string };

  // Server to Client
  notification: Notification;
  "user-online": { userId: string };
  "user-offline": { userId: string };
  "analytics-update": { dashboardId: string; widgetId: string; data: any };
  "ml-prediction-ready": { modelId: string; prediction: any };
  "marketplace-quote": { quoteId: string; data: any };
  "team-update": { teamId: string; action: string; data: any };
}

// ===== ANALYTICS & МОНИТОРИНГ =====

export interface NotificationAnalytics {
  user_id: string;
  notification_id: string;
  event: "sent" | "delivered" | "read" | "clicked" | "dismissed";
  timestamp: string;
}

export interface NotificationEngagementStats {
  total_notifications: number;
  read_count: number;
  engagement_rate: number;
  preferred_channels: NotificationChannel[];
}

// ===== АНАЛИТИКА И ML =====

export enum AnalyticsWidgetType {
  CHART = "chart",
  METRIC = "metric",
  TABLE = "table",
  HEATMAP = "heatmap",
  SCATTER = "scatter",
  FUNNEL = "funnel",
  GAUGE = "gauge",
  PROGRESS = "progress",
}

export enum ChartType {
  LINE = "line",
  BAR = "bar",
  PIE = "pie",
  DOUGHNUT = "doughnut",
  AREA = "area",
  RADAR = "radar",
  BUBBLE = "bubble",
  CANDLESTICK = "candlestick",
}

export enum MLModelType {
  REGRESSION = "regression",
  CLASSIFICATION = "classification",
  CLUSTERING = "clustering",
  TIME_SERIES = "time_series",
  ANOMALY_DETECTION = "anomaly_detection",
  RECOMMENDATION = "recommendation",
}

export enum MLAlgorithm {
  LINEAR_REGRESSION = "linear_regression",
  RANDOM_FOREST = "random_forest",
  XGBOOST = "xgboost",
  NEURAL_NETWORK = "neural_network",
  K_MEANS = "k_means",
  DBSCAN = "dbscan",
  LSTM = "lstm",
  ARIMA = "arima",
}

export enum MLModelStatus {
  TRAINING = "training",
  TRAINED = "trained",
  DEPLOYED = "deployed",
  FAILED = "failed",
  ARCHIVED = "archived",
}

export interface IAnalyticsDashboard {
  id: string;
  name: string;
  description?: string;
  widgets: IAnalyticsWidget[];
  layout: IDashboardLayout;
  filters: IAnalyticsFilter[];
  dateRange: IDateRange;
  refreshInterval: number;
  isPublic: boolean;
  ownerId: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface IAnalyticsWidget {
  id: string;
  type: AnalyticsWidgetType;
  title: string;
  description?: string;
  chartType: ChartType;
  dataSource: IDataSource;
  config: IWidgetConfig;
  position: IWidgetPosition;
  size: IWidgetSize;
  filters: IAnalyticsFilter[];
  drillDownEnabled: boolean;
  realTimeEnabled: boolean;
  mlEnabled: boolean;
  mlModel?: IMLModel;
}

export interface IMLModel {
  id: string;
  name: string;
  type: MLModelType;
  algorithm: MLAlgorithm;
  accuracy: number;
  lastTrained: Date;
  deployedAt?: Date;
  features: string[];
  predictions: IMLPrediction[];
  config: IMLConfig;
  status: MLModelStatus;
}

export interface IMLPrediction {
  id: string;
  modelId: string;
  input: Record<string, unknown>;
  output: Record<string, unknown>;
  confidence: number;
  timestamp: Date;
  metadata?: Record<string, unknown>;
}

export interface IDataSource {
  id: string;
  name: string;
  type: DataSourceType;
  connection: IDataConnection;
  query: string;
  parameters: Record<string, unknown>;
  refreshInterval: number;
  lastUpdated: Date;
  schema: IDataSchema;
}

export interface IDataConnection {
  type: "postgresql" | "mysql" | "mongodb" | "redis" | "api" | "file";
  host: string;
  port: number;
  database: string;
  username: string;
  password: string;
  ssl: boolean;
}

export interface IDataSchema {
  tables: ITableSchema[];
  relationships: IRelationship[];
}

export interface ITableSchema {
  name: string;
  columns: IColumnSchema[];
}

export interface IColumnSchema {
  name: string;
  type: string;
  nullable: boolean;
  primaryKey: boolean;
  foreignKey?: string;
}

export interface IRelationship {
  from: string;
  to: string;
  type: "one_to_one" | "one_to_many" | "many_to_many";
  foreignKey: string;
}

export interface IDashboardLayout {
  type: "grid" | "flexible" | "responsive";
  columns: number;
  rows: number;
  cellSize: { width: number; height: number };
}

export interface IWidgetPosition {
  x: number;
  y: number;
  z: number;
}

export interface IWidgetSize {
  width: number;
  height: number;
  minWidth: number;
  minHeight: number;
  maxWidth: number;
  maxHeight: number;
}

export interface IWidgetConfig {
  colors: string[];
  animations: boolean;
  tooltips: boolean;
  legends: boolean;
  grid: boolean;
  axis: IAxisConfig;
  thresholds: IThreshold[];
}

export interface IAxisConfig {
  x: IAxis;
  y: IAxis;
}

export interface IAxis {
  title: string;
  type: "linear" | "log" | "time" | "category";
  min?: number;
  max?: number;
  ticks: number;
  format: string;
}

export interface IThreshold {
  value: number;
  color: string;
  label: string;
  operator: "gt" | "gte" | "lt" | "lte" | "eq";
}

export interface IAnalyticsFilter {
  id: string;
  field: string;
  operator: FilterOperator;
  value: any;
  type: FilterType;
}

export enum FilterOperator {
  EQUALS = "equals",
  NOT_EQUALS = "not_equals",
  GREATER_THAN = "greater_than",
  LESS_THAN = "less_than",
  CONTAINS = "contains",
  NOT_CONTAINS = "not_contains",
  IN = "in",
  NOT_IN = "not_in",
  BETWEEN = "between",
  IS_NULL = "is_null",
  IS_NOT_NULL = "is_not_null",
}

export enum FilterType {
  STRING = "string",
  NUMBER = "number",
  DATE = "date",
  BOOLEAN = "boolean",
  ARRAY = "array",
}

export interface IDateRange {
  startDate: Date;
  endDate: Date;
  preset?: DateRangePreset;
}

export enum DateRangePreset {
  TODAY = "today",
  YESTERDAY = "yesterday",
  LAST_7_DAYS = "last_7_days",
  LAST_30_DAYS = "last_30_days",
  LAST_90_DAYS = "last_90_days",
  THIS_MONTH = "this_month",
  LAST_MONTH = "last_month",
  THIS_YEAR = "this_year",
  LAST_YEAR = "last_year",
  CUSTOM = "custom",
}

export enum DataSourceType {
  DATABASE = "database",
  API = "api",
  FILE = "file",
  STREAM = "stream",
  CACHE = "cache",
}

export interface IMLConfig {
  trainingDataSize: number;
  validationSplit: number;
  hyperparameters: Record<string, unknown>;
  featureEngineering: IFeatureEngineering[];
  crossValidation: ICrossValidation;
  preprocessing: IPreprocessing;
}

export interface IFeatureEngineering {
  type: FeatureType;
  column: string;
  transformation: string;
  parameters?: Record<string, unknown>;
}

export interface ICrossValidation {
  enabled: boolean;
  folds: number;
  strategy: "kfold" | "stratified" | "time_series";
  metrics: string[];
}

export interface IPreprocessing {
  scaling: ScalingType;
  encoding: EncodingType;
  missingValues: MissingValueStrategy;
  outliers: OutlierStrategy;
}

export enum FeatureType {
  NUMERICAL = "numerical",
  CATEGORICAL = "categorical",
  TEMPORAL = "temporal",
  TEXT = "text",
  IMAGE = "image",
}

export enum ScalingType {
  STANDARD = "standard",
  MINMAX = "minmax",
  ROBUST = "robust",
  NONE = "none",
}

export enum EncodingType {
  ONE_HOT = "one_hot",
  LABEL = "label",
  TARGET = "target",
  HASH = "hash",
}

export enum MissingValueStrategy {
  DROP = "drop",
  MEAN = "mean",
  MEDIAN = "median",
  MODE = "mode",
  INTERPOLATE = "interpolate",
}

export enum OutlierStrategy {
  DROP = "drop",
  CAP = "cap",
  TRANSFORM = "transform",
  IGNORE = "ignore",
}

// ===== MARKETPLACE =====

export enum SupplierStatus {
  ACTIVE = "active",
  INACTIVE = "inactive",
  SUSPENDED = "suspended",
  PENDING = "pending",
  VERIFIED = "verified",
}

export enum VerificationLevel {
  NONE = "none",
  BASIC = "basic",
  VERIFIED = "verified",
  PREMIUM = "premium",
}

export enum ServiceCategory {
  VENUE = "venue",
  CATERING = "catering",
  PHOTOGRAPHY = "photography",
  VIDEOGRAPHY = "videography",
  MUSIC = "music",
  DECORATION = "decoration",
  TRANSPORTATION = "transportation",
  ENTERTAINMENT = "entertainment",
  TECHNICAL = "technical",
  SECURITY = "security",
  OTHER = "other",
}

export interface Supplier {
  id: string;
  name: string;
  description?: string;
  email: string;
  phone: string;
  website?: string;
  avatar?: string;
  coverImage?: string;
  socialMedia?: {
    instagram?: string;
    facebook?: string;
    linkedin?: string;
    telegram?: string;
  };
  location: {
    address: string;
    city: string;
    country: string;
    coordinates: {
      lat: number;
      lng: number;
    };
    serviceRadius: number;
  };
  status: SupplierStatus;
  isVerified: boolean;
  verificationLevel: VerificationLevel;
  rating: {
    average: number;
    totalReviews: number;
    breakdown: {
      quality: number;
      timing: number;
      communication: number;
      priceValue: number;
    };
  };
  services: SupplierService[];
  categories: ServiceCategory[];
  eventTypes: string[];
  availability: {
    isAvailable: boolean;
    workingHours: WorkingHours[];
    busyDates: Date[];
    timezone: string;
  };
  portfolio: PortfolioItem[];
  stats?: SupplierStats;
  performance?: SupplierPerformance;
  createdAt: Date;
  updatedAt: Date;
}

export interface SupplierService {
  id: string;
  name: string;
  description: string;
  category: ServiceCategory;
  price: {
    min: number;
    max: number;
    currency: string;
    perUnit: string;
  };
  availability: boolean;
  leadTime: number;
  features: string[];
  images: string[];

  // Extended fields for PROMPT_06 Marketplace
  supplierId?: string;
  basePrice?: number;
  currency?: string;
  pricingModel?: "per_hour" | "per_day" | "per_event" | "fixed";
  rating?: number;
  reviewCount?: number;
  imageUrl?: string;
  available?: boolean;
}

export interface WorkingHours {
  day: number;
  open: string;
  close: string;
  isWorking: boolean;
}

export interface PortfolioItem {
  id: string;
  title: string;
  description: string;
  images: string[];
  eventType: string;
  date: Date;
  clientFeedback?: string;
}

export interface SupplierStats {
  totalEvents: number;
  totalRevenue: number;
  averageRating: number;
  completionRate: number;
  responseTime: number;
}

export interface SupplierPerformance {
  monthlyRevenue: number[];
  monthlyEvents: number[];
  customerSatisfaction: number;
  repeatCustomers: number;
}

// ===== КОМАНДЫ =====

export enum TeamRole {
  OWNER = "owner",
  ADMIN = "admin",
  MEMBER = "member",
  VIEWER = "viewer",
}

export enum TeamMemberStatus {
  ACTIVE = "active",
  INVITED = "invited",
  SUSPENDED = "suspended",
  LEFT = "left",
}

export enum TeamType {
  ORGANIZATION = "organization",
  PROJECT = "project",
  DEPARTMENT = "department",
  WORKGROUP = "workgroup",
}

export enum TeamStatus {
  ACTIVE = "active",
  INACTIVE = "inactive",
  ARCHIVED = "archived",
}

export interface Team {
  id: string;
  name: string;
  description?: string;
  type: TeamType;
  status: TeamStatus;
  avatar?: string;
  coverImage?: string;
  settings: TeamSettings;
  stats: TeamStats;
  members: TeamMember[];
  createdAt: Date;
  updatedAt: Date;
}

export interface TeamMember {
  id: string;
  userId: string;
  teamId: string;
  role: TeamRole;
  status: TeamMemberStatus;
  joinedAt: Date;
  invitedBy?: string;
  permissions: string[];
}

export interface TeamSettings {
  allowMemberInvites: boolean;
  requireApproval: boolean;
  maxMembers: number;
  privacy: "public" | "private" | "secret";
  notifications: {
    email: boolean;
    push: boolean;
    inApp: boolean;
  };
}

export interface TeamStats {
  totalMembers: number;
  activeMembers: number;
  totalProjects: number;
  totalEvents: number;
  totalRevenue: number;
}

// ===== ПАРТНЕРЫ =====

export enum PartnerIndustry {
  TECHNOLOGY = "technology",
  FINANCE = "finance",
  HEALTHCARE = "healthcare",
  EDUCATION = "education",
  RETAIL = "retail",
  MANUFACTURING = "manufacturing",
  SERVICES = "services",
  OTHER = "other",
}

export enum PartnerType {
  SUPPLIER = "supplier",
  CLIENT = "client",
  VENDOR = "vendor",
  AFFILIATE = "affiliate",
  STRATEGIC = "strategic",
}

export enum PartnershipType {
  EXCLUSIVE = "exclusive",
  NON_EXCLUSIVE = "non_exclusive",
  JOINT_VENTURE = "joint_venture",
  FRANCHISE = "franchise",
}

export interface Partner {
  id: string;
  name: string;
  type: PartnerType;
  industry: PartnerIndustry;
  description?: string;
  website?: string;
  logo?: string;
  contactInfo: PartnerContactInfo;
  address: PartnerAddress;
  partnerships: Partnership[];
  stats: PartnerStats;
  createdAt: Date;
  updatedAt: Date;
}

export interface PartnerContactInfo {
  email: string;
  phone: string;
  contactPerson: string;
  position: string;
}

export interface PartnerAddress {
  street: string;
  city: string;
  state: string;
  country: string;
  postalCode: string;
  coordinates?: {
    lat: number;
    lng: number;
  };
}

export interface Partnership {
  id: string;
  partnerId: string;
  type: PartnershipType;
  startDate: Date;
  endDate?: Date;
  terms: string;
  status: "active" | "inactive" | "expired";
  revenue: number;
  events: number;
}

export interface PartnerStats {
  totalPartnerships: number;
  totalRevenue: number;
  totalEvents: number;
  averageRating: number;
  responseTime: number;
}

// ===== РЕФЕРАЛЬНАЯ СИСТЕМА =====

export enum ReferralEarningStatus {
  PENDING = "pending",
  APPROVED = "approved",
  PAID = "paid",
  CANCELLED = "cancelled",
}

export enum ReferralTransactionType {
  INVITE = "invite",
  PURCHASE = "purchase",
  SUBSCRIPTION = "subscription",
  WITHDRAWAL = "withdrawal",
}

export interface ReferralUser {
  id: string;
  userId: string;
  referralCode: string;
  referredBy?: string;
  level: ReferralLevel;
  stats: ReferralStats;
  earnings: ReferralEarning[];
  invites: ReferralInvite[];
  createdAt: Date;
  updatedAt: Date;
}

export interface ReferralLevel {
  level: number;
  name: string;
  commissionRate: number;
  requirements: {
    totalReferrals: number;
    totalEarnings: number;
    activeReferrals: number;
  };
}

export interface ReferralStats {
  totalReferrals: number;
  activeReferrals: number;
  totalEarnings: number;
  pendingEarnings: number;
  paidEarnings: number;
  conversionRate: number;
}

export interface ReferralEarning {
  id: string;
  userId: string;
  type: ReferralTransactionType;
  amount: number;
  status: ReferralEarningStatus;
  description: string;
  transactionId?: string;
  createdAt: Date;
  paidAt?: Date;
}

export interface ReferralInvite {
  id: string;
  userId: string;
  email: string;
  status: "pending" | "accepted" | "expired";
  expiresAt: Date;
  acceptedAt?: Date;
  earnings?: ReferralEarning[];
}

// ===== AI ПОДДЕРЖКА =====

export interface AIRequest {
  type:
    | "generate_content"
    | "suggestions"
    | "recommendations"
    | "analyze_text"
    | "optimize_content";
  prompt: string;
  context?: Record<string, unknown>;
  options?: {
    maxLength?: number;
    temperature?: number;
    language?: string;
    style?: string;
  };
}

export interface AIResponse {
  id: string;
  content: string;
  suggestions?: string[];
  recommendations?: unknown[];
  analysis?: Record<string, unknown>;
  confidence: number;
  metadata?: Record<string, unknown>;
  createdAt: Date;
}

// ===== ДОКУМЕНТЫ =====

export enum DocumentType {
  CONTRACT = "contract",
  INVOICE = "invoice",
  REPORT = "report",
  AGREEMENT = "agreement",
  PROPOSAL = "proposal",
  CERTIFICATE = "certificate",
}

export enum ContractType {
  SUPPLY = "supply",
  SERVICE = "service",
  LEASE = "lease",
  EMPLOYMENT = "employment",
  PARTNERSHIP = "partnership",
  LICENSING = "licensing",
}

export enum ContractStatus {
  DRAFT = "draft",
  ACTIVE = "active",
  EXPIRED = "expired",
  TERMINATED = "terminated",
  PENDING_APPROVAL = "pending_approval",
  NEGOTIATION = "negotiation",
}

export enum InvoiceType {
  INCOMING = "incoming",
  OUTGOING = "outgoing",
}

export enum InvoiceStatus {
  DRAFT = "draft",
  SENT = "sent",
  PAID = "paid",
  OVERDUE = "overdue",
  CANCELLED = "cancelled",
  PARTIALLY_PAID = "partially_paid",
  FAILED = "failed",
}

export enum ReportType {
  FINANCIAL = "financial",
  OPERATIONAL = "operational",
  ANALYTICAL = "analytical",
  COMPLIANCE = "compliance",
  PERFORMANCE = "performance",
  AUDIT = "audit",
}

export enum ReportStatus {
  DRAFT = "draft",
  COMPLETED = "completed",
  ARCHIVED = "archived",
  IN_REVIEW = "in_review",
  APPROVED = "approved",
}

export interface Contract {
  id: string;
  title: string;
  contractNumber: string;
  type: ContractType;
  status: ContractStatus;
  counterparty: string;
  startDate: Date;
  endDate: Date;
  value: number;
  currency: string;
  description?: string;
  terms?: string;
  attachments?: string[];
  createdBy: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface DocumentInvoice {
  id: string;
  invoiceNumber: string;
  type: InvoiceType;
  status: InvoiceStatus;
  supplier: string;
  customer: string;
  amount: number;
  currency: string;
  dueDate: Date;
  issuedDate: Date;
  paidDate?: Date;
  description?: string;
  lineItems?: InvoiceLineItem[];
  createdBy: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface InvoiceLineItem {
  id: string;
  description: string;
  quantity: number;
  unitPrice: number;
  total: number;
  tax?: number;
}

export interface DocumentReport {
  id: string;
  title: string;
  type: ReportType;
  status: ReportStatus;
  category: string;
  description: string;
  content?: string;
  attachments?: string[];
  generatedBy: string;
  approvedBy?: string;
  createdAt: Date;
  updatedAt: Date;
}

// ===== ФИНАНСЫ =====

export enum TransactionType {
  INCOME = "income",
  EXPENSE = "expense",
  TRANSFER = "transfer",
  REFUND = "refund",
  ADJUSTMENT = "adjustment",
}

export enum TransactionStatus {
  PENDING = "pending",
  COMPLETED = "completed",
  CANCELLED = "cancelled",
  FAILED = "failed",
  PROCESSING = "processing",
}

export enum PaymentStatus {
  PENDING = "pending",
  COMPLETED = "completed",
  FAILED = "failed",
  CANCELLED = "cancelled",
}

export enum PaymentMethod {
  CARD = "card",
  BANK_TRANSFER = "bank_transfer",
  CASH = "cash",
  DIGITAL_WALLET = "digital_wallet",
}

export interface Payment {
  id: string;
  invoiceNumber: string;
  description: string;
  amount: number;
  currency?: string;
  payer: string;
  recipient: string;
  paymentDate: Date;
  status: PaymentStatus;
  paymentMethod: PaymentMethod;
  createdAt: Date;
  updatedAt: Date;
}

export enum BudgetCategory {
  MARKETING = "marketing",
  OPERATIONS = "operations",
  PERSONNEL = "personnel",
  TECHNOLOGY = "technology",
  FACILITIES = "facilities",
  TRAVEL = "travel",
  SUPPLIES = "supplies",
  OTHER = "other",
}

export interface Transaction {
  id: string;
  type: TransactionType;
  amount: number;
  currency: string;
  description: string;
  category: BudgetCategory;
  date: Date;
  status: TransactionStatus;
  reference: string;
  relatedTransactionId?: string;
  metadata?: Record<string, unknown>;
  createdAt: Date;
  updatedAt: Date;
}

// Приоритеты бюджета
export enum BudgetPriority {
  LOW = "low",
  MEDIUM = "medium",
  HIGH = "high",
  CRITICAL = "critical",
}

// Статусы бюджета — единый источник истины в events-unified.ts
export { BudgetStatus };

// Типы статей бюджета
export enum BudgetItemType {
  INCOME = "income",
  EXPENSE = "expense",
  TRANSFER = "transfer",
  REFUND = "refund",
}

// Периоды бюджета
export enum BudgetPeriod {
  DAILY = "daily",
  WEEKLY = "weekly",
  MONTHLY = "monthly",
  QUARTERLY = "quarterly",
  YEARLY = "yearly",
}

export interface BudgetItem {
  id: string;
  title: string;
  description: string;
  type: BudgetItemType;
  category: BudgetCategory;
  priority: BudgetPriority;
  status: BudgetStatus;

  // Финансовые данные
  plannedAmount: number;
  actualAmount: number;
  variance: number;
  currency?: string;

  // Временные рамки
  startDate: Date;
  endDate: Date;
  period: BudgetPeriod;
  year: number;
  month?: number;

  // Дополнительная информация
  vendor?: string;
  invoiceNumber?: string;
  notes?: string;

  // Вложения
  attachments?: Array<{
    id: string;
    name: string;
    type: "pdf" | "image" | "document";
    url: string;
  }>;

  // Повторяющиеся платежи
  isRecurring?: boolean;
  recurringPeriod?: BudgetPeriod;

  // Системные поля
  createdAt: Date;
  updatedAt: Date;
}

/**
 * Сводка бюджета в том виде, в каком её читают экраны.
 *
 * AD-469: поля разделены по тому, кто их действительно заполняет. Сводка
 * платформы (`GET /finance/budget/summary`) и сводка события
 * (`GET /events/:id/budget/summary`) — разные ответы, а тип был один и требовал
 * ВСЕ поля. Экраны добивали недостающее нулями, и «Критичных: 0» означало не
 * «ни одной критичной статьи», а «сервер об этом не говорил». Необязательность
 * здесь значит ровно одно: этот источник такого числа не даёт, и придумывать
 * его нельзя. Гарантированный набор платформенной сводки — `PlatformBudgetSummary`.
 */
export interface BudgetSummary {
  totalPlanned: number;
  totalActual: number;
  /** Отклонение = факт − план, то есть положительное значение — ПЕРЕРАСХОД. */
  totalVariance: number;
  remaining: number;
  utilization: number;
  categories?: string[];
  currency?: string;

  // Детализация по типам — приходит только из сводки события
  totalIncome?: number;
  totalExpenses?: number;
  netProfit?: number;

  // Статусы — `paidAmount` даёт сводка события; двух других не даёт ни один
  // источник, поэтому они необязательны, а не нулевые
  paidAmount?: number;
  pendingAmount?: number;
  overdueAmount?: number;

  // Приоритеты — их считает только сводка платформы
  criticalItems?: number;
  highPriorityItems?: number;
  mediumPriorityItems?: number;
  lowPriorityItems?: number;
}

/**
 * Сводка платформенного бюджета: сервер считает все четыре приоритета и
 * подписывает ответ валютой расчётов, поэтому здесь эти поля обязательны
 * (AD-469). Экран, объявивший этот тип, не имеет права на `?? 0`.
 */
export interface PlatformBudgetSummary extends BudgetSummary {
  currency: string;
  criticalItems: number;
  highPriorityItems: number;
  mediumPriorityItems: number;
  lowPriorityItems: number;
}

export interface FinanceData {
  totalRevenue: number;
  totalExpenses: number;
  profit: number;
  pendingPayments: number;
  completedTransactions: number;
  monthlyGrowth: number;
  currency: string;
}

export interface FinancialTrend {
  period: string;
  revenue: number;
  expenses: number;
  profit: number;
  growth: number;
}

export interface FinancialInsight {
  id: string;
  type: "trend" | "anomaly" | "opportunity" | "risk";
  title: string;
  description: string;
  impact: "low" | "medium" | "high";
  confidence: number;
  data: Record<string, unknown>;
}

export interface FinancialPrediction {
  id: string;
  metric: string;
  predictedValue: number;
  confidence: number;
  timeframe: string;
  factors: string[];
}

// ===== ОБЩИЕ ТИПЫ =====

export interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
  errors?: string[];
}

export interface TimestampFields {
  createdAt: Date;
  updatedAt: Date;
}

export interface IdentityFields {
  id: string;
}

// ===== ФИНАНСОВЫЙ РАЗДЕЛ - ДОПОЛНИТЕЛЬНЫЕ ТИПЫ =====

// Валюты для финансовых документов
export enum Currency {
  RUB = "RUB", // Российский рубль
  USD = "USD", // Доллар США
  EUR = "EUR", // Евро
  CNY = "CNY", // Китайский юань
  JPY = "JPY", // Японская иена
  GBP = "GBP", // Британский фунт
  CHF = "CHF", // Швейцарский франк
  CAD = "CAD", // Канадский доллар
  AUD = "AUD", // Австралийский доллар
  KRW = "KRW", // Южнокорейская вона
}

// Форматы отчетов
export enum ReportFormat {
  PDF = "pdf",
  EXCEL = "excel",
  CSV = "csv",
  JSON = "json",
  XML = "xml",
}

// Локализация отчетов
export enum ReportLocale {
  RU = "ru",
  EN = "en",
  DE = "de",
  FR = "fr",
  ES = "es",
  IT = "it",
  PT = "pt",
  ZH = "zh",
  JA = "ja",
  KO = "ko",
}

// Страны для отчетов
export enum ReportCountry {
  RUSSIA = "RU",
  GERMANY = "DE",
  USA = "US",
  FRANCE = "FR",
  SPAIN = "ES",
  ITALY = "IT",
  PORTUGAL = "PT",
  CHINA = "CN",
  JAPAN = "JP",
  SOUTH_KOREA = "KR",
}

// Локализация счетов
export enum InvoiceLocale {
  RU = "ru",
  EN = "en",
  DE = "de",
  FR = "fr",
  ES = "es",
  IT = "it",
  PT = "pt",
  ZH = "zh",
  JA = "ja",
  KO = "ko",
}

// Страны для счетов
export enum InvoiceCountry {
  RUSSIA = "RU",
  GERMANY = "DE",
  USA = "US",
  FRANCE = "FR",
  SPAIN = "ES",
  ITALY = "IT",
  PORTUGAL = "PT",
  CHINA = "CN",
  JAPAN = "JP",
  SOUTH_KOREA = "KR",
}

// Реферальная система - дополнительные типы (детализация)
export enum ReferralEarningStatusDetail {
  PENDING_EARNING = "PENDING",
  CONFIRMED = "CONFIRMED",
  EXPIRED = "EXPIRED",
  USED = "USED",
}

export enum ReferralTransactionTypeDetail {
  EARNING = "EARNING",
  SPENDING = "SPENDING",
  EXPIRATION = "EXPIRATION",
}

export enum ReferralTransactionStatus {
  COMPLETED = "COMPLETED",
  PENDING = "PENDING",
  FAILED = "FAILED",
}

// Финансовая аналитика
export enum FinancialTrendType {
  INCREASING = "increasing",
  DECREASING = "decreasing",
  STABLE = "stable",
}

export enum AIInsightType {
  OPPORTUNITY = "opportunity",
  RISK = "risk",
  WARNING = "warning",
  RECOMMENDATION = "recommendation",
}

export enum AlertSeverity {
  CRITICAL = "critical",
  WARNING = "warning",
  INFO = "info",
}

export enum AlertType {
  BUDGET_EXCEEDED = "budget_exceeded",
  LOW_PROFIT = "low_profit",
  HIGH_EXPENSES = "high_expenses",
  OPPORTUNITY = "opportunity",
}
